import React, { useMemo, useState } from 'react';
import { Card, SectionHeader, Button, CustomSelect, Icon } from '../../components/ui';
import { SupportErrorMessage } from '../../../../components/overlays/EngineeringSupportBanner';
import {
  useApprovePendingStaffRequestMutation,
  useListPendingStaffRequestsQuery,
  useRejectPendingStaffRequestMutation,
} from '../../../../state/domainRouterApi';
import { useSchoolContext } from '../../../../state/useSchoolContext';

type ApprovalRole = 'admin' | 'principal' | 'teacher' | 'nurse' | 'accounts';

const ROLE_OPTIONS: Array<{ label: string; value: ApprovalRole }> = [
  { label: 'Teacher', value: 'teacher' },
  { label: 'Principal', value: 'principal' },
  { label: 'Nurse', value: 'nurse' },
  { label: 'Accounts', value: 'accounts' },
  { label: 'Admin', value: 'admin' },
];

const toText = (value: unknown): string => String(value ?? '').trim();

const formatDateTime = (iso: string): string => {
  if (!iso) return 'Unknown time';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const displayName = (firstName: string | null, lastName: string | null): string => {
  const name = `${toText(firstName)} ${toText(lastName)}`.trim();
  return name || 'Staff signup request';
};

export default function PendingStaffRequestsSection() {
  const schoolContext = useSchoolContext();
  const activeSchoolId = schoolContext.activeSchoolId ?? undefined;
  const pendingRequestsQuery = useListPendingStaffRequestsQuery({
    status: 'pending',
    schoolId: activeSchoolId,
  });
  const [approvePendingStaffRequest, approveState] = useApprovePendingStaffRequestMutation();
  const [rejectPendingStaffRequest, rejectState] = useRejectPendingStaffRequestMutation();
  const [roleByRequestId, setRoleByRequestId] = useState<Record<string, ApprovalRole>>({});
  const [operationError, setOperationError] = useState('');

  const requests = useMemo(
    () => [...(pendingRequestsQuery.data?.items ?? [])],
    [pendingRequestsQuery.data?.items],
  );
  const isSaving = approveState.isLoading || rejectState.isLoading;

  const readRole = (requestId: string): ApprovalRole => roleByRequestId[requestId] ?? 'teacher';

  const handleApprove = async (requestId: string) => {
    setOperationError('');
    try {
      await approvePendingStaffRequest({
        requestId,
        role: readRole(requestId),
        schoolId: activeSchoolId,
      }).unwrap();
    } catch (error: unknown) {
      setOperationError(error instanceof Error ? error.message : 'Failed to approve staff request.');
    }
  };

  const handleReject = async (requestId: string) => {
    setOperationError('');
    try {
      await rejectPendingStaffRequest({ requestId, schoolId: activeSchoolId }).unwrap();
    } catch (error: unknown) {
      setOperationError(error instanceof Error ? error.message : 'Failed to reject staff request.');
    }
  };

  return (
    <Card data-testid="settings-users-pending-staff-requests-section">
      <SectionHeader
        title="Pending Staff Requests"
        description="Review staff self-signups that did not use an active code or approved school email domain."
      />

      {operationError ? (
        <SupportErrorMessage className="mb-4">
          {operationError}
        </SupportErrorMessage>
      ) : null}

      {pendingRequestsQuery.isLoading ? (
        <p className="text-sm text-text-secondary">Loading pending staff requests…</p>
      ) : requests.length === 0 ? (
        <div
          data-testid="settings-users-pending-staff-empty"
          className="rounded-lg border border-border bg-muted px-4 py-6 text-center text-sm text-text-secondary"
        >
          No pending staff requests. Staff who sign up without a valid code or approved domain will appear here.
        </div>
      ) : (
        <div className="space-y-3" data-testid="settings-users-pending-staff-list">
          {requests.map((request) => (
            <div
              key={request.requestId}
              data-testid={`settings-users-pending-staff-row-${request.requestId}`}
              className="flex flex-col gap-4 rounded-lg border border-border bg-white p-4 lg:flex-row lg:items-center lg:justify-between"
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-semibold text-irongrey">
                    {displayName(request.firstName, request.lastName)}
                  </p>
                  <span className="rounded-full bg-warning-background px-2.5 py-0.5 text-xs font-medium text-irongrey">
                    Pending approval
                  </span>
                </div>
                <p className="mt-1 break-all text-sm text-text-secondary">{request.email}</p>
                <p className="mt-1 text-xs text-text-tertiary">
                  Requested {formatDateTime(request.requestedAt)}
                  {request.source ? ` via ${request.source}` : ''}
                </p>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="w-full sm:w-44">
                  <CustomSelect
                    testId={`settings-users-pending-staff-role-${request.requestId}`}
                    options={ROLE_OPTIONS}
                    value={readRole(request.requestId)}
                    onChange={(value) =>
                      setRoleByRequestId((previous) => ({
                        ...previous,
                        [request.requestId]: value as ApprovalRole,
                      }))
                    }
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    data-testid={`settings-users-pending-staff-approve-${request.requestId}`}
                    variant="primary"
                    disabled={isSaving}
                    onClick={() => void handleApprove(request.requestId)}
                  >
                    <Icon name="Check" className="mr-1 h-4 w-4" />
                    Approve
                  </Button>
                  <Button
                    data-testid={`settings-users-pending-staff-reject-${request.requestId}`}
                    variant="outline"
                    disabled={isSaving}
                    onClick={() => void handleReject(request.requestId)}
                  >
                    Reject
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
